import { FC, useCallback, useMemo } from 'react';

import { useRecoilState, useRecoilValue } from 'recoil';

import { SellerNeedAccountMonthItem } from '@shared/api/needAccount/needAccount.interface';
import { useAuth } from '@shared/state/hooks/useAuth';
import { getAccountDate } from '@shared/utils/dateUtil';
import { formatDate } from '@shared/utils/formatDate';
import { Checkbox, Table, TableColumnsType } from 'antd';
import _ from 'lodash';

import { SellerAccountDetailModalButton } from '../SellerAccountDetailModal/SellerAccountDetailModalButton';
import {
  SELLER_BRAND_ETC_ID,
  sellerBrandOptionsAtom,
  sellerSelectedBrandIdsAtom,
} from './accountContent.atom';
import { SellerAccountButton } from './SellerAccountButton';

//* 이번달 및 다음달 정산 받아야하는 내용 표시

interface Props {
  needAccountMonthly: SellerNeedAccountMonthItem[];
}

type AccountData = {
  id: string;
  year: number;
  month: number;
  accountDate: Date;
  accountCount: number;
  accountPrice: number;
  rebatePrice: number;
};

const columns: TableColumnsType<AccountData> = [
  {
    title: '정산기간',
    dataIndex: 'month',
    key: 'month',
    align: 'center',
    render: (_: number, record) => {
      const now = new Date();
      return (
        <p
          className={`whitespace-pre px-6 py-1 text-base font-medium ${
            now.getTime() > getAccountDate(record.month).getTime()
              ? 'theme-text-warn-03'
              : ''
          }`}
        >
          {record.year % 100}년 {record.month}월
        </p>
      );
    },
  },
  {
    title: '정산지급일',
    dataIndex: 'accountDate',
    key: 'accountDate',
    align: 'center',
    render: (accountDate: Date) => {
      const now = new Date();
      if (accountDate.getTime() < now.getTime()) {
        return (
          <p className="px-6 py-1 text-base font-medium theme-text-warn-03">
            <span className="whitespace-pre">
              {formatDate(accountDate, { contains: { year: false } })}
            </span>
            <span className="break-keep"> (미지급)</span>
          </p>
        );
      } else {
        return (
          <p className="px-6 py-1 text-base font-medium">
            <span className="whitespace-pre">
              {formatDate(accountDate, { contains: { year: false } })}
            </span>
          </p>
        );
      }
    },
  },
  {
    title: '총 판매건수',
    dataIndex: 'accountCount',
    key: 'accountCount',
    align: 'center',
    render: (accountCount: number, record) => {
      const now = new Date();
      return (
        <p
          className={`whitespace-pre px-6 py-1 text-base font-medium ${
            now.getTime() > getAccountDate(record.month).getTime()
              ? 'theme-text-warn-03'
              : ''
          }`}
        >
          {accountCount.toLocaleString()}개
        </p>
      );
    },
  },
  {
    title: '화환 정산금',
    dataIndex: 'accountPrice',
    key: 'accountPrice',
    align: 'center',
    render: (accountPrice: number, record) => {
      const now = new Date();
      return (
        <p
          className={`whitespace-pre px-6 py-1 text-base font-medium ${
            now.getTime() > getAccountDate(record.month).getTime()
              ? 'theme-text-warn-03'
              : ''
          }`}
        >
          {accountPrice.toLocaleString()}원
        </p>
      );
    },
  },
  {
    title: '상조 정산금',
    dataIndex: 'rebatePrice',
    key: 'rebatePrice',
    align: 'center',
    render: (rebatePrice: number, record) => {
      const now = new Date();
      return (
        <p
          className={`whitespace-pre px-6 py-1 text-base font-medium ${
            now.getTime() > getAccountDate(record.month).getTime()
              ? 'theme-text-warn-03'
              : ''
          }`}
        >
          {rebatePrice.toLocaleString()}원
        </p>
      );
    },
  },
  {
    title: '합계',
    key: 'total',
    align: 'center',
    render: (_, record) => {
      const now = new Date();
      return (
        <p
          className={`whitespace-pre px-6 py-1 text-base font-medium ${
            now.getTime() > getAccountDate(record.month).getTime()
              ? 'theme-text-warn-03'
              : ''
          }`}
        >
          {(record.accountPrice + record.rebatePrice).toLocaleString()}원
        </p>
      );
    },
  },
  {
    title: '세부내역',
    key: 'detail',
    align: 'center',
    render: (_, record) => {
      return (
        <div className="center-box">
          <SellerAccountDetailModalButton
            year={record.year}
            month={record.month}
            type="need-account"
          />
        </div>
      );
    },
  },
];

export const PresentAccountContent: FC<Props> = function PresentAccountContent({
  needAccountMonthly,
}: Props) {
  const { isAdmin } = useAuth();

  // admin일 때만 정산 컬럼 추가
  const finalColumns: TableColumnsType<AccountData> = useMemo(() => {
    if (!isAdmin()) return columns;
    return [
      ...columns,
      {
        title: '정산',
        key: 'account',
        align: 'center' as const,
        render: (_: unknown, record: AccountData) => (
          <div className="center-box">
            <SellerAccountButton year={record.year} month={record.month} />
          </div>
        ),
      },
    ];
  }, [isAdmin]);

  // BugoBrand Filtering (ID 기반)
  const brandOptions = useRecoilValue(sellerBrandOptionsAtom);
  const [selectedBrandIds, setSelectedBrandIds] = useRecoilState(
    sellerSelectedBrandIdsAtom,
  );

  const isAllSelected =
    brandOptions.length > 0 && brandOptions.every((o) => selectedBrandIds.has(o.id));

  const toggleBrand = useCallback(
    (id: string) => {
      setSelectedBrandIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [setSelectedBrandIds],
  );

  const toggleAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedBrandIds(new Set());
    } else {
      setSelectedBrandIds(new Set(brandOptions.map((o) => o.id)));
    }
  }, [isAllSelected, brandOptions, setSelectedBrandIds]);

  // etc 판별용: 실제 등록 브랜드 ID 목록 (etc 제외)
  const knownBrandIds = useMemo(
    () =>
      new Set(brandOptions.filter((o) => o.id !== SELLER_BRAND_ETC_ID).map((o) => o.id)),
    [brandOptions],
  );

  // 월별 데이터의 brands 배열 내에서 선택된 bugoBrand만 필터 후 재집계
  const tableData: AccountData[] = useMemo(() => {
    const isEtcSelected = selectedBrandIds.has(SELLER_BRAND_ETC_ID);
    return _.sortBy(needAccountMonthly, ['year', 'month'])
      .map((monthItem) => {
        const filteredBrands = monthItem.brands.filter((b) => {
          const isKnown = knownBrandIds.has(b.bugoBrandId);
          return isKnown ? selectedBrandIds.has(b.bugoBrandId) : isEtcSelected;
        });
        return {
          id: `needAccount-${monthItem.year}-${monthItem.month}`,
          year: monthItem.year,
          month: monthItem.month,
          accountDate: monthItem.expectedAccountDate
            ? new Date(monthItem.expectedAccountDate)
            : getAccountDate(monthItem.month),
          accountCount: _.sumBy(filteredBrands, 'totalCount'),
          accountPrice: _.sumBy(filteredBrands, 'totalPriceWhole'),
          rebatePrice: _.sumBy(filteredBrands, 'totalPriceRebate'),
        };
      })
      .filter((row) => row.accountCount > 0);
  }, [needAccountMonthly, selectedBrandIds, knownBrandIds]);

  const presentMonthAccount = useMemo(() => {
    return (
      <Table
        className="w-full"
        columns={finalColumns}
        dataSource={tableData}
        pagination={{
          hideOnSinglePage: true,
        }}
        rowKey={'id'}
        bordered
        size={'small'}
      />
    );
  }, [tableData, finalColumns]);

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-4 border px-4 text-sm theme-bg-1 theme-border-1">
        <div className={`center-box self-stretch bg-opacity-40 px-2 font-bold`}>상조</div>
        <div className="flex flex-1 flex-wrap items-center gap-3 py-1 pr-2">
          <Checkbox checked={isAllSelected} onChange={toggleAll}>
            전체
          </Checkbox>
          {brandOptions.map((opt) => (
            <Checkbox
              key={opt.id}
              checked={selectedBrandIds.has(opt.id)}
              onChange={() => toggleBrand(opt.id)}
            >
              {opt.label}
            </Checkbox>
          ))}
        </div>
      </div>
      <p className="pl-4 text-base font-medium">정산예정내역</p>
      {presentMonthAccount}
    </div>
  );
};
